import { useMemo } from 'react';
import QRCode from 'qrcode';
import {
  cleanNH12,
  scanUrl,
} from "@shared/identity/neohealthcard-id-adapter";

const CARD_BG = 'linear-gradient(155deg, #E8EDF3, #DEE4EC, #D4DCE6)';
const CARD_TEAL = '#00B8B8';
const CARD_NAVY = '#002B5C';
const CARD_INK = '#0B2545';
const CARD_MUT = '#5D7285';
const CARD_FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const CARD_MONO = "'SF Mono','Fira Code',ui-monospace,monospace";

const SHEEN_ID = 'nhc-patient-sheen';
const SHEEN_CSS = `
@keyframes nhcSheenMove {
  0% { transform: translateX(-120%) skewX(-18deg); }
  100% { transform: translateX(360%) skewX(-18deg); }
}
@media (prefers-reduced-motion: reduce) {
  .${SHEEN_ID} { animation: none !important; }
}
`;

function CardMark({ width = 30, opacity = 1 }: { width?: number; opacity?: number }) {
  const h = Math.round(width * (140 / 220));
  return (
    <svg width={width} height={h} viewBox="0 0 220 140" fill="none" style={{ opacity }}>
      <circle cx="73" cy="70" r="66" fill={CARD_TEAL} />
      <circle cx="147" cy="70" r="66" fill={CARD_NAVY} />
      <clipPath id="cardMk"><circle cx="147" cy="70" r="66" /></clipPath>
      <circle cx="73" cy="70" r="66" fill={CARD_TEAL} clipPath="url(#cardMk)" opacity="0.9" />
      <path d="M52,70 L86,70 L96,84 L110,36 L124,84 L134,70 L168,70" stroke="#fff" strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function RealQR({ data, size = 68 }: { data: string; size?: number }) {
  const matrix = useMemo(() => {
    try {
      const qr = QRCode.create(data, { errorCorrectionLevel: 'H' });
      return { n: qr.modules.size, get: (r: number, c: number) => qr.modules.get(r, c) };
    } catch {
      return null;
    }
  }, [data]);

  if (!matrix) return null;
  const { n, get } = matrix;
  const FINDER = 7;
  const inFinder = (r: number, c: number) =>
    (r < FINDER && c < FINDER) || (r < FINDER && c >= n - FINDER) || (r >= n - FINDER && c < FINDER);

  const dots: React.ReactNode[] = [];
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      if (inFinder(r, c)) continue;
      if (get(r, c)) dots.push(<circle key={`${r}-${c}`} cx={c + 0.5} cy={r + 0.5} r={0.42} fill={CARD_NAVY} />);
    }
  }

  const finders = [[0, 0], [n - FINDER, 0], [0, n - FINDER]];
  return (
    <svg width={size} height={size} viewBox={`0 0 ${n} ${n}`} style={{ borderRadius: 4, display: 'block' }}>
      <rect width={n} height={n} fill="#fff" rx="1.5" />
      {dots}
      {finders.map(([x, y], i) => (
        <g key={i}>
          <rect x={x + 0.5} y={y + 0.5} width="6" height="6" rx="1.6" fill="none" stroke={CARD_NAVY} strokeWidth="0.9" />
          <rect x={x + 2} y={y + 2} width="3" height="3" rx="1" fill={CARD_NAVY} />
        </g>
      ))}
      <rect x={Math.floor(n / 2) - 1.5} y={Math.floor(n / 2) - 1.5} width="3" height="3" rx="0.8" fill={CARD_TEAL} />
    </svg>
  );
}

export interface ApprovedPatientCardProps {
  name: string;
  nhcId: string;
  nhcIdRaw?: string;
  bloodGroup?: string;
  insuranceStatus?: string;
  details?: { label: string; value: string }[];
  footer?: string;
}

export default function ApprovedPatientCard({
  name,
  nhcId,
  nhcIdRaw,
  bloodGroup,
  insuranceStatus,
  details,
  footer = 'Verified Provider Network · Every access is logged',
}: ApprovedPatientCardProps) {
  const blood = bloodGroup ?? details?.find(d => d.label === 'Blood')?.value ?? 'N/A';
  const insurance = insuranceStatus ?? details?.find(d => d.label === 'Insurance')?.value ?? '—';

  const qrData = useMemo(() => {
    const raw = nhcIdRaw || nhcId;
    const cleaned = cleanNH12(raw);
    return cleaned.length === 12 ? scanUrl(raw) : `nhc:${raw}`;
  }, [nhcIdRaw, nhcId]);

  return (
    <>
      <style>{SHEEN_CSS}</style>
      <div style={{
        width: '100%', maxWidth: 400,
        aspectRatio: '85.6 / 53.98',
        background: CARD_BG, borderRadius: 18,
        padding: '20px 22px 0', position: 'relative', overflow: 'hidden',
        boxShadow: '0 12px 40px rgba(10,37,64,0.10), 0 2px 8px rgba(10,37,64,0.06)',
        fontFamily: CARD_FONT, display: 'flex', flexDirection: 'column',
        boxSizing: 'border-box',
      }}>
        {/* Sheen animation */}
        <div className={SHEEN_ID} style={{
          position: 'absolute', top: '-30%', left: 0,
          width: '45%', height: '160%', pointerEvents: 'none',
          transform: 'skewX(-18deg)',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent)',
          filter: 'blur(2px)',
          animation: 'nhcSheenMove 5.5s ease-in-out infinite',
          zIndex: 2,
        }} />

        {/* Watermark */}
        <div style={{ position: 'absolute', right: -10, top: '28%', pointerEvents: 'none' }}>
          <CardMark width={160} opacity={0.07} />
        </div>

        {/* Top: Logo + Badge */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CardMark width={30} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, color: CARD_INK, letterSpacing: '-0.01em' }}>NeoHealthCard™</div>
              <div style={{ fontSize: 9, color: CARD_MUT, fontWeight: 600, marginTop: 1 }}>Universal Health Identity</div>
            </div>
          </div>
          <span style={{
            fontSize: 8, fontWeight: 800, letterSpacing: '0.06em',
            color: '#fff', background: CARD_TEAL,
            padding: '5px 10px', borderRadius: 6,
          }}>VERIFIED PATIENT</span>
        </div>

        {/* Name + 12-digit ID */}
        <div style={{ marginTop: 14, position: 'relative', zIndex: 1, flex: 1 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: CARD_INK, letterSpacing: '-0.01em', lineHeight: 1.2 }}>{name}</div>
          <div style={{ fontFamily: CARD_MONO, fontSize: 15, fontWeight: 700, color: CARD_INK, letterSpacing: '0.08em', marginTop: 4, opacity: 0.85 }}>{nhcId}</div>
        </div>

        {/* Bottom: QR + NFC/QR + Blood/Insurance */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, position: 'relative', zIndex: 1, paddingBottom: 10 }}>
          <div style={{ background: '#fff', borderRadius: 8, padding: 5, border: '1px solid rgba(0,0,0,0.06)', flexShrink: 0 }}>
            <RealQR data={qrData} size={56} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingBottom: 3 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10B981' }} />
              <span style={{ fontSize: 9.5, fontWeight: 700, color: CARD_TEAL }}>NFC Ready</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10B981' }} />
              <span style={{ fontSize: 9.5, fontWeight: 700, color: CARD_TEAL }}>QR Enabled</span>
            </div>
          </div>

          <div style={{ marginLeft: 'auto', display: 'flex', gap: 16, paddingBottom: 3 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 8, fontWeight: 600, color: CARD_MUT, letterSpacing: '0.04em', textTransform: 'uppercase' as const }}>Blood</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: CARD_INK, marginTop: 1 }}>{blood}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 8, fontWeight: 600, color: CARD_MUT, letterSpacing: '0.04em', textTransform: 'uppercase' as const }}>Insurance</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: CARD_INK, marginTop: 1 }}>{insurance === 'Linked' ? 'Linked' : '—'}</div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          borderTop: '1px solid rgba(0,184,184,0.15)',
          padding: '6px 0', textAlign: 'center',
          fontSize: 8.5, fontWeight: 600, color: `${CARD_TEAL}90`, letterSpacing: '0.02em',
        }}>{footer}</div>
      </div>
    </>
  );
}
